import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import {
  Box,
  CircularProgress,
  IconButton,
  Link,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Button from '../../../components/common/Button';
import Loading from '../../../components/common/Loading';
import Notification from '../../../components/common/Notification';
import Tabs from '../../../components/common/Tabs';
import AdditionalInfo from '../../../components/module/rec/applicant/AdditionalInfo';
import History from '../../../components/module/rec/applicant/History';
import Profile from '../../../components/module/rec/applicant/Profile';
import { useNotification } from '../../../hooks/useNotification';
import { PAGE_IDS } from '../../../constants/permissions';
import { JIRA_TICKET_URL } from '../../../utils/constants';
import { applicantService } from '../../../services/applicant.service';
import { menuService } from '../../../services/menu.service';
import api from '../../../services/httpClient';
import { APPLICANT_ENDPOINTS } from '../../../services/endpoints/rec/applicant.endpoints';
import DuplicateApplicants from '../../../components/module/rec/applicant/DuplicateApplicants';
import Messages from '../../../components/module/rec/applicant/Messages';
import Comments from '../../../components/module/rec/applicant/Comments';
import { useSidebar } from '../../../context/SidebarContext';
import LinkedInConnectionStatusIcon from '../../../components/common/icons/LinkedInConnectionStatusIcon';
import { getLinkedInConnectionStatusTooltip, hasLinkedInConnectionStatus } from '../../../utils/linkedInConnectionStatus';
import { normalizeApplicantBasicInfo } from '../../../utils/messageUtils';

export interface ApplicantProfileData {
  basicInfo: {
    fullName?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    nic?: string;
    vacancy?: string;
    vacancyCode?: string;
    dob?: string;
    contactNoMobile?: string;
    contactNoResident?: string;
    address?: string;
    linkedInUrl?: string;
    linkedInConnectionStatus?: string | null;
    issueId?: string;
    currentAssignee?: string;
    currentStatus?: string;
    currentStatusId?: number;
    currentDesignation?: string;
    currentDesignationId?: number;
    elapsedTime: number;
    totalExperienceYears: number;
    totalExperienceMonths: number;
    createdAt: string;
    source: string;
    sourceReferral: string;
    currentSalary: number | null;
    expectedSalary: number | null;
    joiningDate: string;
    joiningNote: string;
    noticePeriod: string;
    currentStatusColour?: string;
    currentStatusType?: string;
    previousStatus?: string;
    previousStatusColour?: string;
    previousStatusType?: string;
    cvLink?: string;
    vacancyDesignation?: string;
    vacancyDesignationId?: number;
    latestInterview?: string;
    vacancyCountry?: string;
    communication_medium?: 'WHATSAPP' | 'EMAIL' | 'ZALO';
  };
  educations: {
    instituteId: number;
    institute: string;
    degree: string;
    from: string;
    to: string;
    isEdit?: boolean;
  }[];
  experiences: {
    companyId: number;
    company: string;
    companyUrl: string;
    role: string;
    from: string;
    to: string;
    experienceYears: number;
    experienceMonths: number;
    isEdit?: boolean;
    technologies?: {
      id: number;
      name: string;
    }[];
  }[];
  languages: {
    id: number;
    name: string;
  }[];
  skills: {
    id: number;
    name: string;
  }[];
  technologies: {
    id: number;
    name: string;
  }[];
}

/** Aligns with `renderContent` tab order (0 = Profile … 5 = Messages). */
export type ApplicantProfileEntryTab = 'profile' | 'history' | 'additional' | 'duplicates' | 'messages';

const TAB_INDEX_BY_ENTRY: Record<ApplicantProfileEntryTab, number> = {
  profile: 0,
  history: 1,
  additional: 2,
  duplicates: 3,
  // Comments is index 4; Messages is index 5 — resolved by label at runtime.
  messages: 5,
};

/**
 * Resolves the initial tab value from loaded menu tabs.
 * When `entryTab` is 'messages', searches by label (case-insensitive) first,
 * then falls back to index 5. Returns undefined (default to first tab) when
 * `entryTab` is undefined or the target tab is not present in the menu data.
 */
function resolveInitialTabValue(
  tabs: { label: string; value: any }[],
  entryTab: ApplicantProfileEntryTab | undefined,
): any | undefined {
  if (!entryTab || tabs.length === 0) return undefined;

  if (entryTab === 'messages') {
    // Prefer label match (case-insensitive) — robust against index shifts.
    const byLabel = tabs.find((t) => t.label?.toLowerCase() === 'messages');
    if (byLabel) return byLabel.value;
    // Index-5 fallback for backward compat.
    return tabs[5]?.value;
  }

  const index = TAB_INDEX_BY_ENTRY[entryTab];
  return tabs[index]?.value;
}

/** Matches fixed `.profile-sidebar` width; collapsed strip holds the toggle (8px grid). */
const APPLICANT_RIGHT_SIDEBAR_EXPANDED_PX = 290;
const APPLICANT_RIGHT_SIDEBAR_COLLAPSED_PX = 48;
/** Used when the API sends no status colour (e.g. a newly added Jira status). */
const SIDEBAR_FALLBACK_COLOUR = '#702DFF';

const ApplicantProfile: React.FC<{
  userEmail?: string;
  initialProfileTab?: ApplicantProfileEntryTab;
}> = ({ userEmail, initialProfileTab }) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [tabs, setTabs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<any>();
  const [isGeneratingCV, setIsGeneratingCV] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const { notification, hideNotification, showNotification } = useNotification();
  const [summaryData, setSummaryData] = useState<ApplicantProfileData['basicInfo'] | null>(null);
  const theme = useTheme();
  const isViewportBelowLg = useMediaQuery(theme.breakpoints.down('lg'));
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(true);
  const { updateMenu } = useSidebar();
  const [isCVGenerating, setIsCVGenerating] = useState(false);

  useEffect(() => {
    if (isViewportBelowLg) {
      setIsRightSidebarOpen(false);
    }
  }, [isViewportBelowLg]);

  useEffect(() => {
    updateMenu(2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch basic info directly so the right sidebar is always populated,
  // regardless of which tab is active (e.g. direct link with ?tab=message).
  useEffect(() => {
    if (!id) return;
    setNotFound(false);
    api
      .get<{ data: ApplicantProfileData['basicInfo'] }>(APPLICANT_ENDPOINTS.basic(id), {
        headers: { 'x-api-id': 27, 'x-page-id': PAGE_IDS.APPLICANT_HISTORY },
      })
      .then((res) => {
        setSummaryData(
          normalizeApplicantBasicInfo(res.data.data as Record<string, unknown>) as ApplicantProfileData['basicInfo'],
        );
      })
      .catch((err) => {
        console.error('Error fetching applicant basic info:', err);
        setNotFound(true);
      });
  }, [id]);

  useEffect(() => {
    const fetchTabs = async () => {
      try {
        setIsLoading(true);
        const response = await menuService.getTabs(PAGE_IDS.APPLICANT_HISTORY);
        const raw = response.data.data ?? [];
        const mappedTabs = raw.map((tab) => ({
          label: tab.name,
          value: tab.menuId,
        }));
        setTabs(mappedTabs);

        // Priority: 1) ?tab query param (must be a valid ApplicantProfileEntryTab), 2) initialProfileTab prop, 3) default (index 0)
        const rawTabParam = searchParams.get('tab');
        const validTabs: ApplicantProfileEntryTab[] = ['profile', 'history', 'additional', 'duplicates', 'messages'];
        const tabParam = validTabs.includes(rawTabParam as ApplicantProfileEntryTab)
          ? (rawTabParam as ApplicantProfileEntryTab)
          : null;
        const effectiveEntry: ApplicantProfileEntryTab | undefined =
          tabParam ?? initialProfileTab ?? undefined;

        const resolved = resolveInitialTabValue(mappedTabs, effectiveEntry);
        const fallback = mappedTabs[0]?.value;
        setActiveTab(resolved ?? fallback);
      } catch (error) {
        console.error('Error fetching tabs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTabs();
  }, [initialProfileTab, searchParams]);


  const handleTabChange = (value: any) => {
    setActiveTab(value);
  };

  const renderContent = () => {
    switch (activeTab) {
      case tabs[0]?.value:
        return <Profile setSummary={setSummaryData} />;
      case tabs[1]?.value:
        return <History setSummary={setSummaryData} />;
      case tabs[2]?.value:
        return <AdditionalInfo setSummary={setSummaryData} />;
      case tabs[3]?.value:
        return <DuplicateApplicants applicantEmail={summaryData?.email} jiraId={summaryData?.issueId} />;
      case tabs[4]?.value:
        return <Comments />;
      case tabs[5]?.value:
        return (
          <Messages
            applicantName={summaryData?.fullName || summaryData?.firstName || "Applicant"}
            applicantMobile={summaryData?.contactNoMobile}
            communicationMedium={summaryData?.communication_medium}
          />
        );
      default:
        return null;
    }
  };

  const getSidebarBackground = () => {
    const currentColour = summaryData?.currentStatusColour || SIDEBAR_FALLBACK_COLOUR;
    const previousColour = summaryData?.previousStatusColour;

    if (['initial', 'final'].includes(summaryData?.currentStatusType ?? '') || !summaryData?.previousStatus || !previousColour) {
      return currentColour;
    }

    return `linear-gradient(180deg, ${currentColour}, ${previousColour})`;
  }

  const generateCV = async () => {
    if (userEmail && id) {
      setIsGeneratingCV(true);
      applicantService
        .generateCvEmail(Number(id), userEmail)
        .then((response) => {
          setIsGeneratingCV(false);
          if (response.status === 200) {
            showNotification('CV generated successfully and sent to your email.', 'success');
          } else {
            showNotification('Error generating CV. Please try again.', 'error');
          }
        })
        .catch(() => {
          setIsGeneratingCV(false);
          showNotification('Error generating CV. Please try again.', 'error');
        });
    }
  };

  const CVgenerate = async () => {
    if (userEmail && id) {
      setIsCVGenerating(true);
      applicantService
        .generateCvEmail(Number(id), userEmail)
        .then((response) => {
          setIsCVGenerating(false);
          if (response.status === 200) {
            showNotification('CV generated successfully and sent to your email.', 'success');
          } else {
            showNotification('Error generating CV. Please try again.', 'error');
          }
        })
        .catch(() => {
          setIsCVGenerating(false);
          showNotification('Error generating CV. Please try again.', 'error');
        });
    }
  };

  const rightSidebarPaddingPx = summaryData
    ? isRightSidebarOpen
      ? APPLICANT_RIGHT_SIDEBAR_EXPANDED_PX
      : APPLICANT_RIGHT_SIDEBAR_COLLAPSED_PX
    : 0;

  const applicantSidebarClassName = [
    'profile-sidebar',
    'applicant-profile-sidebar',
    !isRightSidebarOpen ? 'applicant-profile-sidebar--collapsed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <Box
        className={[
          'page-container applicant-profile-page-container',
          !summaryData
            ? 'applicant-profile-page-container--no-sidebar'
            : !isRightSidebarOpen
              ? 'applicant-profile-page-container--sidebar-collapsed'
              : '',
        ]
          .filter(Boolean)
          .join(' ')}
        data-testid="applicant-profile-main"
        sx={{
          flex: 1,
          overflowY: 'auto',
          boxSizing: 'border-box',
          paddingRight: `${rightSidebarPaddingPx}px`,
          transition: 'padding-right 0.3s ease',
        }}
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Loading />
          </Box>
        ) : notFound ? (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="300px" gap={2}>
            <Typography variant="h6" color="text.secondary">
              Applicant not found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The applicant with ID <strong>{id}</strong> does not exist or you don't have access.
            </Typography>
          </Box>
        ) : (
          <Box flex={1}>
            <Typography className="page-title" mb={3}>
              Applicant Profile
            </Typography>
            <Box mb={3}>
              <Tabs
                tabs={tabs}
                value={activeTab}
                onChange={handleTabChange}
              />
            </Box>
            {renderContent()}
          </Box>
        )}
      </Box>

      {/* Right Sidebar */}
      {summaryData && (
        <Box className={applicantSidebarClassName} sx={{ background: getSidebarBackground() }}>
          <IconButton
            type="button"
            data-testid="applicant-right-sidebar-toggle"
            aria-expanded={isRightSidebarOpen}
            aria-label={
              isRightSidebarOpen ? 'Collapse applicant summary' : 'Expand applicant summary'
            }
            onClick={() => setIsRightSidebarOpen((open) => !open)}
            size="small"
            sx={{
              position: 'absolute',
              left: 8,
              top: 16,
              zIndex: 3,
              color: '#FFFFFF',
              width: 32,
              height: 32,
              '&:focus-visible': {
                outline: '2px solid #6941C6',
                outlineOffset: 2,
              },
            }}
          >
            {isRightSidebarOpen ? (
              <ChevronRight sx={{ fontSize: 19 }} />
            ) : (
              <ChevronLeft sx={{ fontSize: 19 }} />
            )}
          </IconButton>
          <Box className="applicant-profile-sidebar__inner" sx={{ width: '100%', pt: 7, boxSizing: 'border-box' }}>
            {/* Avatar */}
            <Box
              mt={5}
              display="flex"
              flexDirection="column"
              gap={1.5}
              alignItems="center"
              justifyContent="center"
            >
              <Box flexShrink={0}>
                {/* Use UserProfileIcon */}
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_667_7968)">
                    <path d="M32 16C32 20.4768 30.1613 24.5238 27.1982 27.4277C26.9377 27.683 26.6681 27.9295 26.3904 28.1667C26.2799 28.2612 26.1683 28.3547 26.0549 28.4457C25.6476 28.7757 25.2238 29.0861 24.7847 29.3748C22.2631 31.0346 19.2442 32 16 32C12.7558 32 9.73686 31.0346 7.21528 29.3748C6.77618 29.0861 6.35243 28.776 5.94507 28.4457C5.83172 28.3547 5.72011 28.2612 5.60956 28.1667C5.33194 27.9295 5.06234 27.6826 4.80181 27.4277C1.8387 24.5235 0 20.4768 0 16C0 7.16331 7.16331 0 16 0C24.8367 0 32 7.16331 32 16Z" fill="white" />
                    <path d="M26.0552 28.4459C25.6478 28.7759 25.2241 29.0863 24.785 29.3751C22.2634 31.0348 19.2445 32.0002 16.0002 32.0002C12.756 32.0002 9.7371 31.0348 7.21552 29.3751C6.77642 29.0863 6.35267 28.7762 5.94531 28.4459C6.21456 26.3903 7.49139 24.5931 9.34788 23.6731C8.30855 23.2929 7.55731 22.6624 7.10008 22.1856C6.79212 21.8682 6.77293 21.3771 7.04741 21.0308C7.81749 20.0679 9.24708 18.2051 9.26138 13.9209V11.4467C9.26138 7.7934 12.3229 4.70752 16.0006 4.70752C19.6783 4.70752 22.7395 7.7934 22.7395 11.4467V13.8536C22.7395 18.1859 24.1788 20.0626 24.9538 21.0308C25.2282 21.3771 25.2091 21.8682 24.9008 22.1856C24.4435 22.6624 23.6877 23.2929 22.6481 23.6731C24.5087 24.5928 25.7856 26.3903 26.0555 28.4459H26.0552Z" fill="#702DFF" />
                  </g>
                  <defs>
                    <clipPath id="clip0_667_7968">
                      <rect width="32" height="32" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </Box>
              <div className="profile-avatar-title">
                {summaryData?.fullName ? summaryData?.fullName : 'N/A'}
              </div>
              {summaryData?.vacancyCode ? (
                <Typography
                  className="profile-avatar-subtitle"
                  data-testid="applicant-sidebar-vacancy-code"
                >
                  {summaryData.vacancyCode}
                </Typography>
              ) : null}
            </Box>
            <Box width="100%" px={4} mt={3} mb={3}>
              <svg width="232" height="1" viewBox="0 0 232 1" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line y1="0.5" x2="232" y2="0.5" stroke="url(#paint0_linear_1444_11290)" stroke-opacity="0.6" />
                <defs>
                  <linearGradient id="paint0_linear_1444_11290" x1="0" y1="1.5" x2="232" y2="1.5" gradientUnits="userSpaceOnUse">
                    <stop stop-color="white" stop-opacity="0" />
                    <stop offset="0.519231" stop-color="white" />
                    <stop offset="1" stop-color="white" stop-opacity="0" />
                  </linearGradient>
                </defs>
              </svg>
            </Box>
            {/* Status and Elapsed Time */}
            <Box textAlign="center" mb={2}>
              <Typography className='profile-icon-title' sx={{ mb: 2 }}>{summaryData?.currentStatus ? summaryData?.currentStatus : 'N/A'}</Typography>
              <Typography className='profile-icon-title'>
                Elapsed Time: {`${summaryData?.elapsedTime} ${summaryData?.elapsedTime > 1 ? 'Days' : 'Day'}`}
              </Typography>
              {summaryData?.currentStatus && !['OpenCV', 'Short Listed', 'No Vacancy'].includes(summaryData.currentStatus) && (
                <Typography className='profile-icon-title' sx={{ mt: 2.5 }}>
                  Interview: {summaryData?.latestInterview ? summaryData.latestInterview : 'N/A'}
                </Typography>
              )}
            </Box>
            <Box width="100%" px={4} mb={2}>
              <svg width="232" height="1" viewBox="0 0 232 1" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line y1="0.5" x2="232" y2="0.5" stroke="url(#paint0_linear_1444_11290)" stroke-opacity="0.6" />
                <defs>
                  <linearGradient id="paint0_linear_1444_11290" x1="0" y1="1.5" x2="232" y2="1.5" gradientUnits="userSpaceOnUse">
                    <stop stop-color="white" stop-opacity="0" />
                    <stop offset="0.519231" stop-color="white" />
                    <stop offset="1" stop-color="white" stop-opacity="0" />
                  </linearGradient>
                </defs>
              </svg>
            </Box>
            {/* Assigned To */}
            <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
              <Box className='profile-sidebar-action-icon' mb={1}>
                {/* Assignee Icon */}
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="40" height="40" rx="20" fill="black" fill-opacity="0.15" />
                  <g clip-path="url(#clip0_1444_11299)">
                    <path d="M25.333 28.125V26.2083C25.333 25.1917 24.9291 24.2166 24.2103 23.4978C23.4914 22.7789 22.5163 22.375 21.4997 22.375H14.7913C13.7747 22.375 12.7997 22.7789 12.0808 23.4978C11.3619 24.2166 10.958 25.1917 10.958 26.2083V28.125" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M26.292 18.5417L28.2087 20.4583L32.042 16.625" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M18.1458 18.5417C20.2629 18.5417 21.9792 16.8254 21.9792 14.7083C21.9792 12.5912 20.2629 10.875 18.1458 10.875C16.0287 10.875 14.3125 12.5912 14.3125 14.7083C14.3125 16.8254 16.0287 18.5417 18.1458 18.5417Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </g>
                  <defs>
                    <clipPath id="clip0_1444_11299">
                      <rect width="23" height="23" fill="white" transform="translate(10 8)" />
                    </clipPath>
                  </defs>
                </svg>
              </Box>
              <Typography className='profile-icon-title'>{summaryData?.currentAssignee ? summaryData?.currentAssignee : 'N/A'}</Typography>
            </Box>
            {/* Job Title */}
            <Box display="flex" flexDirection="column" alignItems="center" mb={3} mt={3}>
              <Box className='profile-sidebar-action-icon' mb={1}>
                {/* Briefcase icon (employee-icon.svg) */}
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="40" height="40" rx="20" fill="black" fill-opacity="0.15" />
                  <path d="M27 13H24V12C24 11.4696 23.7893 10.9609 23.4142 10.5858C23.0391 10.2107 22.5304 10 22 10H18C17.4696 10 16.9609 10.2107 16.5858 10.5858C16.2107 10.9609 16 11.4696 16 12V13H13C12.2044 13 11.4413 13.3161 10.8787 13.8787C10.3161 14.4413 10 15.2044 10 16V25C10 25.7956 10.3161 26.5587 10.8787 27.1213C11.4413 27.6839 12.2044 28 13 28H27C27.7956 28 28.5587 27.6839 29.1213 27.1213C29.6839 26.5587 30 25.7956 30 25V16C30 15.2044 29.6839 14.4413 29.1213 13.8787C28.5587 13.3161 27.7956 13 27 13ZM18 12H22V13H18V12ZM28 25C28 25.2652 27.8946 25.5196 27.7071 25.7071C27.5196 25.8946 27.2652 26 27 26H13C12.7348 26 12.4804 25.8946 12.2929 25.7071C12.1054 25.5196 12 25.2652 12 25V19.39L16.68 21C16.7862 21.0144 16.8938 21.0144 17 21H23C23.1084 20.998 23.2161 20.9812 23.32 20.95L28 19.39V25ZM28 17.28L22.84 19H17.16L12 17.28V16C12 15.7348 12.1054 15.4804 12.2929 15.2929C12.4804 15.1054 12.7348 15 13 15H27C27.2652 15 27.5196 15.1054 27.7071 15.2929C27.8946 15.4804 28 15.7348 28 16V17.28Z" fill="white" />
                </svg>
              </Box>
              <Typography className="profile-icon-title">
                {summaryData?.currentDesignation || summaryData?.vacancyDesignation || 'N/A'}
              </Typography>
            </Box>
            <Box width="100%" px={4} mb={2}>
              <svg width="232" height="1" viewBox="0 0 232 1" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line y1="0.5" x2="232" y2="0.5" stroke="url(#paint0_linear_1444_11290)" stroke-opacity="0.6" />
                <defs>
                  <linearGradient id="paint0_linear_1444_11290" x1="0" y1="1.5" x2="232" y2="1.5" gradientUnits="userSpaceOnUse">
                    <stop stop-color="white" stop-opacity="0" />
                    <stop offset="0.519231" stop-color="white" />
                    <stop offset="1" stop-color="white" stop-opacity="0" />
                  </linearGradient>
                </defs>
              </svg>
            </Box>
            {/* Action Icons */}
            <Box display="flex" justifyContent="center" gap={3} mt={2}>
              {/* Jira Issue */}
              <Link className='profile-sidebar-action-icon' href={summaryData?.issueId ? `${JIRA_TICKET_URL}${summaryData?.issueId}` : '#'} target="_blank">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="40" height="40" rx="20" fill="black" fill-opacity="0.15" />
                  <path d="M28.0012 11.3564H18.542C18.542 12.4889 18.9919 13.575 19.7927 14.3758C20.5934 15.1766 21.6795 15.6265 22.812 15.6265H24.5545V17.3089C24.5561 19.665 26.4657 21.5747 28.8218 21.5762V12.177C28.8218 11.724 28.4545 11.3564 28.0012 11.3564Z" fill="white" />
                  <path d="M23.3205 16.0703H13.8613C13.8628 18.4264 15.7724 20.3361 18.1286 20.3377H19.8711V22.0255C19.8741 24.3816 21.785 26.2899 24.1411 26.2899V16.8911C24.1411 16.4378 23.7737 16.0703 23.3205 16.0703Z" fill="white" />
                  <path d="M18.6379 20.7803H9.17871C9.17871 23.1386 11.0905 25.0503 13.4487 25.0503H15.1967V26.7326C15.1982 29.0866 17.1045 30.9954 19.4586 30.9999V21.6009C19.4586 21.1477 19.0911 20.7803 18.6379 20.7803Z" fill="white" />
                </svg>
              </Link>
              {/* Action 2 */}
              <Box
                className='profile-sidebar-action-icon'
                onClick={() => isGeneratingCV ? null : generateCV()}
                sx={{ cursor: isGeneratingCV ? 'not-allowed !important' : 'pointer !important' }}
              >
                {!isGeneratingCV && (
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="40" height="40" rx="20" fill="black" fill-opacity="0.15" />
                    <path d="M19.5 28H14.5C14.2348 28 13.9804 27.8946 13.7929 27.7071C13.6054 27.5196 13.5 27.2652 13.5 27V13C13.5 12.7348 13.6054 12.4804 13.7929 12.2929C13.9804 12.1054 14.2348 12 14.5 12H19.5V15C19.5 15.7956 19.8161 16.5587 20.3787 17.1213C20.9413 17.6839 21.7044 18 22.5 18H25.5C25.5 18.2652 25.6054 18.5196 25.7929 18.7071C25.9804 18.8946 26.2348 19 26.5 19C26.7652 19 27.0196 18.8946 27.2071 18.7071C27.3946 18.5196 27.5 18.2652 27.5 18V17C27.5 17 27.5 17 27.5 16.94C27.4896 16.8481 27.4695 16.7576 27.44 16.67V16.58C27.3919 16.4772 27.3278 16.3827 27.25 16.3L21.25 10.3C21.1673 10.2222 21.0728 10.1581 20.97 10.11C20.9402 10.1058 20.9099 10.1058 20.88 10.11L20.56 10H14.5C13.7044 10 12.9413 10.3161 12.3787 10.8787C11.8161 11.4413 11.5 12.2044 11.5 13V27C11.5 27.7956 11.8161 28.5587 12.3787 29.1213C12.9413 29.6839 13.7044 30 14.5 30H19.5C19.7652 30 20.0196 29.8946 20.2071 29.7071C20.3946 29.5196 20.5 29.2652 20.5 29C20.5 28.7348 20.3946 28.4804 20.2071 28.2929C20.0196 28.1054 19.7652 28 19.5 28ZM21.5 13.41L24.09 16H22.5C22.2348 16 21.9804 15.8946 21.7929 15.7071C21.6054 15.5196 21.5 15.2652 21.5 15V13.41ZM16.5 16C16.2348 16 15.9804 16.1054 15.7929 16.2929C15.6054 16.4804 15.5 16.7348 15.5 17C15.5 17.2652 15.6054 17.5196 15.7929 17.7071C15.9804 17.8946 16.2348 18 16.5 18H17.5C17.7652 18 18.0196 17.8946 18.2071 17.7071C18.3946 17.5196 18.5 17.2652 18.5 17C18.5 16.7348 18.3946 16.4804 18.2071 16.2929C18.0196 16.1054 17.7652 16 17.5 16H16.5ZM19.5 24.05H16.5C16.2348 24.05 15.9804 24.1554 15.7929 24.3429C15.6054 24.5304 15.5 24.7848 15.5 25.05C15.5 25.3152 15.6054 25.5696 15.7929 25.7571C15.9804 25.9446 16.2348 26.05 16.5 26.05H19.5C19.7652 26.05 20.0196 25.9446 20.2071 25.7571C20.3946 25.5696 20.5 25.3152 20.5 25.05C20.5 24.7848 20.3946 24.5304 20.2071 24.3429C20.0196 24.1554 19.7652 24.05 19.5 24.05ZM20.5 20.05H16.5C16.2348 20.05 15.9804 20.1554 15.7929 20.3429C15.6054 20.5304 15.5 20.7848 15.5 21.05C15.5 21.3152 15.6054 21.5696 15.7929 21.7571C15.9804 21.9446 16.2348 22.05 16.5 22.05H20.5C20.7652 22.05 21.0196 21.9446 21.2071 21.7571C21.3946 21.5696 21.5 21.3152 21.5 21.05C21.5 20.7848 21.3946 20.5304 21.2071 20.3429C21.0196 20.1554 20.7652 20.05 20.5 20.05Z" fill="white" />
                    <path d="M22.0945 22.2021L24.3217 20H28.7728L31 22.2088L25.027 28.2261H30.8954L28.6715 30.4349H24.2171L22 28.2228L28.0574 22.2055L22.0945 22.2021Z" fill="white" />
                    <path d="M26.9473 27.2217H29.7853L30.8955 28.2257L27.9833 28.2223L26.9473 27.2217Z" fill="white" />
                    <path d="M25.0306 22.2021L26.5458 23.7081L25.0239 25.2108L22.0947 22.2021H25.0306Z" fill="white" />
                  </svg>
                )}
                {isGeneratingCV && <CircularProgress size={20} sx={{ color: 'white' }} />}
              </Box>
              {/* Action 3 */}
              <Link className='profile-sidebar-action-icon' href={summaryData?.cvLink ? summaryData?.cvLink : '#'} target="_blank">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="40" height="40" rx="20" fill="black" fill-opacity="0.15" />
                  <path d="M17 18H18C18.2652 18 18.5196 17.8946 18.7071 17.7071C18.8946 17.5196 19 17.2652 19 17C19 16.7348 18.8946 16.4804 18.7071 16.2929C18.5196 16.1054 18.2652 16 18 16H17C16.7348 16 16.4804 16.1054 16.2929 16.2929C16.1054 16.4804 16 16.7348 16 17C16 17.2652 16.1054 17.5196 16.2929 17.7071C16.4804 17.8946 16.7348 18 17 18ZM17 20C16.7348 20 16.4804 20.1054 16.2929 20.2929C16.1054 20.4804 16 20.7348 16 21C16 21.2652 16.1054 21.5196 16.2929 21.7071C16.4804 21.8946 16.7348 22 17 22H23C23.2652 22 23.5196 21.8946 23.7071 21.7071C23.8946 21.5196 24 21.2652 24 21C24 20.7348 23.8946 20.4804 23.7071 20.2929C23.5196 20.1054 23.2652 20 23 20H17ZM28 16.94C27.9896 16.8481 27.9695 16.7576 27.94 16.67V16.58C27.8919 16.4772 27.8278 16.3827 27.75 16.3L21.75 10.3C21.6673 10.2222 21.5728 10.1581 21.47 10.11C21.4402 10.1058 21.4099 10.1058 21.38 10.11C21.2784 10.0517 21.1662 10.0143 21.05 10H15C14.2044 10 13.4413 10.3161 12.8787 10.8787C12.3161 11.4413 12 12.2044 12 13V27C12 27.7956 12.3161 28.5587 12.8787 29.1213C13.4413 29.6839 14.2044 30 15 30H25C25.7956 30 26.5587 29.6839 27.1213 29.1213C27.6839 28.5587 28 27.7956 28 27V17C28 17 28 17 28 16.94ZM22 13.41L24.59 16H23C22.7348 16 22.4804 15.8946 22.2929 15.7071C22.1054 15.5196 22 15.2652 22 15V13.41ZM26 27C26 27.2652 25.8946 27.5196 25.7071 27.7071C25.5196 27.8946 25.2652 28 25 28H15C14.7348 28 14.4804 27.8946 14.2929 27.7071C14.1054 27.5196 14 27.2652 14 27V13C14 12.7348 14.1054 12.4804 14.2929 12.2929C14.4804 12.1054 14.7348 12 15 12H20V15C20 15.7956 20.3161 16.5587 20.8787 17.1213C21.4413 17.6839 22.2044 18 23 18H26V27ZM23 24H17C16.7348 24 16.4804 24.1054 16.2929 24.2929C16.1054 24.4804 16 24.7348 16 25C16 25.2652 16.1054 25.5196 16.2929 25.7071C16.4804 25.8946 16.7348 26 17 26H23C23.2652 26 23.5196 25.8946 23.7071 25.7071C23.8946 25.5196 24 25.2652 24 25C24 24.7348 23.8946 24.4804 23.7071 24.2929C23.5196 24.1054 23.2652 24 23 24Z" fill="white" />
                </svg>
              </Link>
              {/* LinkedIn */}
              <Tooltip
                title={
                  getLinkedInConnectionStatusTooltip(summaryData?.linkedInConnectionStatus)
                  ?? 'Open LinkedIn profile'
                }
                arrow
              >
                <Link
                  className={
                    hasLinkedInConnectionStatus(summaryData?.linkedInConnectionStatus)
                      ? undefined
                      : 'profile-sidebar-action-icon'
                  }
                  href={summaryData?.linkedInUrl || 'https://www.linkedin.com/404/'}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={
                    hasLinkedInConnectionStatus(summaryData?.linkedInConnectionStatus)
                      ? { display: 'inline-flex', lineHeight: 0 }
                      : undefined
                  }
                  aria-label={
                    hasLinkedInConnectionStatus(summaryData?.linkedInConnectionStatus)
                      ? getLinkedInConnectionStatusTooltip(summaryData?.linkedInConnectionStatus) ?? undefined
                      : 'Open LinkedIn profile'
                  }
                >
                  {hasLinkedInConnectionStatus(summaryData?.linkedInConnectionStatus) ? (
                    <LinkedInConnectionStatusIcon
                      status={summaryData.linkedInConnectionStatus}
                      variant="card"
                      disableTooltip
                    />
                  ) : (
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="40" height="40" rx="20" fill="black" fillOpacity="0.15" />
                      <path d="M21.8093 21.0281C21.8093 20.7538 21.8093 20.4795 21.8093 20.2052C21.8093 19.7938 22.2138 19.5195 22.4834 19.3824C22.7531 19.1081 23.1576 19.1081 23.5621 19.1081C24.7756 19.1081 25.18 19.9309 25.18 21.3024V26.6509H28.416V20.8909C28.416 17.8738 26.798 16.3652 24.6407 16.3652C24.1014 16.3652 23.4272 16.3652 22.8879 16.7767C22.3486 17.0509 21.9441 17.4624 21.6744 18.0109V16.6395H18.4385V26.6509H21.6744V21.0281H21.8093Z" fill="white" />
                      <path d="M16.6852 16.6396H13.4492V26.6511H16.6852V16.6396Z" fill="white" />
                      <path d="M15.0673 15.1313C15.3369 15.1313 15.6066 15.1313 15.7414 15.1313C15.8763 15.1313 16.1459 14.9941 16.4156 14.7198C16.6852 14.4456 16.6852 14.3084 16.8201 14.1713C16.8201 13.897 16.9549 13.7598 16.9549 13.4856C16.9549 13.2113 16.9549 12.937 16.8201 12.7998C16.6852 12.6627 16.5504 12.3884 16.4156 12.2513C16.2807 12.1141 16.0111 11.977 15.7414 11.8398C15.4718 11.8398 15.2021 11.8398 15.0673 11.8398C14.6628 11.8398 14.1234 11.977 13.8538 12.3884C13.5841 12.6627 13.3145 13.2113 13.3145 13.6227C13.3145 14.0341 13.4493 14.5827 13.8538 14.857C14.1234 15.1313 14.6628 15.4056 15.0673 15.4056V15.1313Z" fill="white" />
                    </svg>
                  )}
                </Link>
              </Tooltip>
            </Box>
            <Box display="flex" justifyContent="center" mt={4} mb={2}>
              <Button
                variant="contained"
                width="220px"
                disableElevation
                isLoading={isCVGenerating}
                onClick={CVgenerate}
                aria-label="Generate CV"
                data-testid="applicant-generate-cv"
                sx={{
                  height: '40px',
                  borderRadius: '8px',
                  fontFamily: 'Inter',
                  fontSize: '16px',
                  fontWeight: 500,
                }}
              >
                Generate CV
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Notification Component */}
      <Notification
        open={notification.open}
        onClose={hideNotification}
        message={notification.message}
        severity={notification.severity}
        title={notification.title}
      />
    </Box>
  );
};

export default ApplicantProfile;
